import { makeStyles } from '@material-ui/core';
import {
  ProxiedSignInPage,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import {
  configApiRef,
  SignInPageProps,
  useApi,
} from '@backstage/core-plugin-api';
import { oidcAuthApiRef } from '../../apis';

const oidcProvider: SignInProviderConfig = {
  id: 'oidc',
  title: 'Sign in with ID',
  message: 'Sign in to the revenexx Developer Portal',
  apiRef: oidcAuthApiRef,
};

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0B0522',
    fontFamily: "'Flink Neue Condensed', sans-serif",
    zIndex: 9999,
  },
  background: {
    position: 'fixed',
    inset: 0,
    zIndex: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    '& img': {
      height: '100%',
      width: '100%',
      objectFit: 'cover',
    },
  },
  noiseOverlay: {
    position: 'absolute',
    inset: 0,
    opacity: 0.4,
    mixBlendMode: 'overlay' as const,
    backgroundImage: "url('/noise.svg')",
  },
  content: {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    animation: '$fadeIn 1.5s cubic-bezier(0.22, 1, 0.36, 1) forwards',
  },
  logo: {
    width: 280,
    filter: 'drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15))',
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  signInWrapper: {
    // Hide the default Backstage header/title bar
    '& header': {
      display: 'none',
    },
    // Remove the outer page container styling
    '& > div': {
      background: 'transparent !important',
      boxShadow: 'none !important',
      padding: '0 !important',
      minHeight: 'unset !important',
    },
    // Override BackstagePage height
    '& [class*="BackstagePage-root"]': {
      minHeight: 'unset !important',
      height: 'auto !important',
      overflow: 'visible !important',
    },
    // Remove any grid/page wrapper padding
    '& [class*="MuiGrid-container"]': {
      justifyContent: 'center',
    },
    // Style the sign-in card
    '& [class*="MuiCard-root"]': {
      backgroundColor: 'rgba(23, 18, 42, 0.85)',
      backdropFilter: 'blur(20px)',
      borderRadius: 16,
      border: '1px solid rgba(98, 13, 255, 0.2)',
      boxShadow:
        '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(98, 13, 255, 0.1)',
      minWidth: 340,
      maxWidth: 400,
    },
    // Card header (title)
    '& [class*="MuiCardHeader-root"]': {
      paddingBottom: 0,
      textAlign: 'center',
      '& [class*="MuiCardHeader-content"]': {
        textAlign: 'center',
      },
    },
    '& [class*="MuiCardHeader-title"]': {
      color: '#FFFFFF',
      fontSize: '1.2rem',
      fontWeight: 500,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      fontFamily: "'Flink Neue Condensed', sans-serif",
      textAlign: 'center',
    },
    // Card subheader (message)
    '& [class*="MuiCardHeader-subheader"]': {
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: '0.85rem',
      fontFamily: "'Flink Neue Condensed', sans-serif",
      marginTop: 4,
      textAlign: 'center',
    },
    // Card content area
    '& [class*="MuiCardContent-root"]': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    // Card actions (button area)
    '& [class*="MuiCardActions-root"]': {
      padding: '8px 16px 16px',
      justifyContent: 'center',
    },
    // Sign In button
    '& [class*="MuiButton-containedPrimary"], & [class*="MuiButton-contained"]':
      {
        backgroundColor: '#620DFF !important',
        color: '#FFFFFF !important',
        padding: '10px 40px',
        fontSize: '0.9rem',
        fontWeight: 500,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        borderRadius: 8,
        fontFamily: "'Flink Neue Condensed', sans-serif",
        boxShadow: 'none',
        width: '100%',
        '&:hover': {
          backgroundColor: '#7B2FFF !important',
          boxShadow: '0 4px 20px rgba(98, 13, 255, 0.4)',
        },
      },
    // Any outlined/text buttons
    '& [class*="MuiButton-outlined"], & [class*="MuiButton-text"]': {
      color: 'rgba(255, 255, 255, 0.6) !important',
      borderColor: 'rgba(255, 255, 255, 0.2) !important',
      fontFamily: "'Flink Neue Condensed', sans-serif",
    },
    // Dividers
    '& [class*="MuiDivider-root"]': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    // Typography overrides
    '& [class*="MuiTypography-root"]': {
      fontFamily: "'Flink Neue Condensed', sans-serif",
    },
    // Progress indicators
    '& [class*="MuiCircularProgress-root"]': {
      color: '#620DFF',
    },
  },
  footer: {
    position: 'fixed',
    bottom: '2rem',
    zIndex: 10,
    display: 'flex',
    gap: '1rem',
    fontSize: '0.7rem',
    fontWeight: 300,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.4)',
    fontFamily: "'Flink Neue Condensed', sans-serif",
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'scale(0.98)',
    },
    to: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
}));

/**
 * Is guest sign-in switched on for this deployment?
 *
 * `auth.guestSignIn` is a key of this app's own (declared in packages/app's
 * `configSchema`), and it exists because the obvious key cannot be read here:
 * `auth.providers.*` carries no frontend visibility, so Backstage strips it from
 * the bundle the browser gets and this component would always see nothing. That
 * is a deliberate protection — provider config holds client secrets — so the
 * answer is a frontend-visible flag rather than a wider visibility on the
 * provider.
 *
 * So the two halves of the guest route read two DIFFERENT keys: this flag, and
 * the provider block the backend gates on. What keeps them from drifting is that
 * both live in app-config.e2e.yaml and nowhere else — one file to add for the
 * harness, one file absent everywhere else. Neither key appears in
 * app-config.yaml or app-config.production.yaml, and a test proves that
 * (packages/backend/src/guestProvider.test.ts).
 *
 * Read through the config API rather than an env var or a build-time flag: the
 * config is what the running deployment actually loaded, which is the thing a
 * guard should ask about. Why this route at all, over a real test user or an
 * injected session: docs/e2e-harness.md.
 */
const useGuestSignIn = (): boolean => {
  const configApi = useApi(configApiRef);
  return configApi.getOptionalBoolean('auth.guestSignIn') ?? false;
};

export const CustomSignInPage = (props: SignInPageProps) => {
  const classes = useStyles();
  const guest = useGuestSignIn();

  /**
   * The end-to-end harness's way in.
   *
   * `ProxiedSignInPage` signs in on mount and renders no interface of its own,
   * which is what the guest provider needs: it is a proxy authenticator, so
   * there is no form and no redirect anywhere. A browser driven by Playwright
   * therefore lands on the portal rather than leaving it on first paint, which
   * is the whole blocker this route exists to remove.
   *
   * Guest cannot be reached in production even if this branch were somehow taken
   * there: the provider refuses to authenticate unless NODE_ENV is
   * `development` or `dangerouslyAllowOutsideDevelopment` is explicitly set.
   * That guard is upstream Backstage's, not ours, and it holds independently of
   * this component — which is why the route is safe rather than merely careful.
   */
  if (guest) {
    return <ProxiedSignInPage {...props} provider="guest" />;
  }

  return (
    <div className={classes.root}>
      <div className={classes.background}>
        <img src="/revenexx-bg.webp" alt="" />
        <div className={classes.noiseOverlay} />
      </div>

      <div className={classes.content}>
        <div className={classes.logo}>
          <img
            src="/revenexx-logo.svg"
            alt="revenexx"
            style={{ width: '100%', height: 'auto' }}
          />
        </div>

        <div className={classes.signInWrapper}>
          <SignInPage {...props} auto providers={[oidcProvider]} />
        </div>
      </div>

      <div className={classes.footer}>
        <span>revenexx GmbH</span>
        <span>•</span>
        <span>All rights reserved</span>
      </div>
    </div>
  );
};
